import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditIcon from '@material-ui/icons/Edit'
import PersonIcon from '@material-ui/icons/Person'
import BusinessIcon from '@material-ui/icons/Business'
import axios from 'axios'
import useClientSearch from './hooks/useClientSearch'

const STATUS_COLORS = {
  draft: '#90a4ae',
  sent: '#42a5f5',
  accepted: '#66bb6a',
  declined: '#ef5350'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 24,
    maxWidth: 1200,
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: '#666'
    }
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f9f9f9'
    }
  },
  statusChip: {
    textTransform: 'capitalize',
    fontWeight: 600,
    fontSize: 11
  },
  dialogContent: {
    minWidth: 480,
    minHeight: 300
  },
  searchResults: {
    maxHeight: 200,
    overflow: 'auto',
    border: '1px solid #eee',
    borderRadius: 4,
    marginTop: 8
  },
  resultItem: {
    borderBottom: '1px solid #f5f5f5'
  },
  sourceChip: {
    fontSize: 10,
    height: 20
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '16px 0',
    color: '#999',
    fontSize: 13
  },
  orLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#e0e0e0'
  }
}))

const ProposalListView = (props) => {
  const classes = useStyles()
  const history = useHistory()
  const url = props.url

  const [proposals, setProposals] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  // New proposal form state
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [manualClientName, setManualClientName] = useState('')
  const [manualContactName, setManualContactName] = useState('')
  const [selectedClient, setSelectedClient] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const { results: searchResults, searching, search, clear: clearSearch } = useClientSearch(url)

  const fetchProposals = useCallback(async () => {
    if (!url) return
    try {
      const res = await axios.get(`${url}/api/mtc-proposals`)
      setProposals(res.data.proposals || [])
    } catch (err) {
      console.error('Failed to fetch proposals:', err)
    } finally {
      setLoading(false)
    }
  }, [url])

  const fetchTemplates = useCallback(async () => {
    if (!url) return
    try {
      const res = await axios.get(`${url}/api/mtc-proposals/templates`)
      setTemplates(res.data || [])
      if (res.data?.length) {
        const def = res.data.find(t => t.isDefault) || res.data[0]
        setSelectedTemplate(def._id)
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err)
    }
  }, [url])

  useEffect(() => {
    fetchProposals()
    fetchTemplates()
  }, [fetchProposals, fetchTemplates])

  const handleSearchChange = (e) => {
    const val = e.target.value
    setSearchQuery(val)
    search(val)
    setSelectedClient(null)
  }

  const handleSelectClient = (client) => {
    setSelectedClient(client)
    setSearchQuery(client.clientName)
    clearSearch()
  }

  const handleCreate = async () => {
    setCreating(true)
    try {
      const body = {
        templateId: selectedTemplate || undefined,
        crmSource: selectedClient
          ? { type: selectedClient.type, sourceId: selectedClient.sourceId }
          : { type: 'manual', sourceId: '' },
        crmSnapshot: selectedClient
          ? {
            clientName: selectedClient.clientName,
            contactName: selectedClient.contactName,
            contactEmail: selectedClient.contactEmail,
            contactPhone: selectedClient.contactPhone,
            clientAddress: selectedClient.clientAddress
          }
          : {
            clientName: manualClientName,
            contactName: manualContactName
          }
      }
      const res = await axios.post(`${url}/api/mtc-proposals`, body)
      setDialogOpen(false)
      history.push(`/mtc-proposals/${res.data._id}`)
    } catch (err) {
      console.error('Failed to create proposal:', err)
      alert('Failed to create proposal')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (e, proposalId) => {
    e.stopPropagation()
    if (!window.confirm('Delete this draft proposal?')) return
    try {
      await axios.delete(`${url}/api/mtc-proposals/${proposalId}`)
      fetchProposals()
    } catch (err) {
      console.error('Failed to delete proposal:', err)
    }
  }

  const resetDialog = () => {
    setManualClientName('')
    setManualContactName('')
    setSelectedClient(null)
    setSearchQuery('')
    clearSearch()
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          MTC Proposals
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => { resetDialog(); setDialogOpen(true) }}
        >
          New Proposal
        </Button>
      </div>

      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ padding: 40 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : proposals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ padding: 40, color: '#999' }}>
                  No proposals yet. Create your first one!
                </TableCell>
              </TableRow>
            ) : (
              proposals.map((p) => (
                <TableRow
                  key={p._id}
                  className={classes.row}
                  onClick={() => history.push(`/mtc-proposals/${p._id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {p.crmSnapshot?.clientName || 'Untitled'}
                    </Typography>
                    {p.crmSnapshot?.contactName && (
                      <Typography variant="caption" color="textSecondary">
                        {p.crmSnapshot.contactName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.status}
                      size="small"
                      className={classes.statusChip}
                      style={{ backgroundColor: STATUS_COLORS[p.status] || '#ccc', color: '#fff' }}
                    />
                  </TableCell>
                  <TableCell>
                    {p.templateId?.name || '—'}
                  </TableCell>
                  <TableCell>
                    {new Date(p.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(p.updatedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); history.push(`/mtc-proposals/${p._id}`) }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {p.status === 'draft' && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => handleDelete(e, p._id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* New Proposal Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Proposal</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {/* Client Search */}
          <Typography variant="subtitle2" gutterBottom>Find Client</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search customers or properties..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: searching ? <CircularProgress size={16} /> : null
            }}
          />
          {searchResults.length > 0 && (
            <List dense className={classes.searchResults}>
              {searchResults.map((r, i) => (
                <ListItem
                  key={i}
                  button
                  className={classes.resultItem}
                  onClick={() => handleSelectClient(r)}
                  selected={selectedClient?.sourceId === r.sourceId}
                >
                  <ListItemText
                    primary={r.clientName}
                    secondary={r.clientAddress}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      size="small"
                      className={classes.sourceChip}
                      icon={r.type === 'customer' ? <PersonIcon style={{ fontSize: 12 }} /> : <BusinessIcon style={{ fontSize: 12 }} />}
                      label={r.type === 'customer' ? 'Customer' : 'Aspire'}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <div className={classes.orDivider}>
            <div className={classes.orLine} />
            <span>or enter manually</span>
            <div className={classes.orLine} />
          </div>

          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Client Name"
            value={selectedClient ? selectedClient.clientName : manualClientName}
            onChange={(e) => { setSelectedClient(null); setManualClientName(e.target.value) }}
            style={{ marginBottom: 12 }}
          />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Contact Name"
            value={selectedClient ? selectedClient.contactName : manualContactName}
            onChange={(e) => { setSelectedClient(null); setManualContactName(e.target.value) }}
            style={{ marginBottom: 16 }}
          />

          {/* Template Selection */}
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Template</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              label="Template"
            >
              {templates.map(t => (
                <MenuItem key={t._id} value={t._id}>
                  {t.name} {t.isDefault ? '(Default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={creating || (!selectedClient && !manualClientName)}
          >
            {creating ? <CircularProgress size={16} /> : 'Create Proposal'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(ProposalListView)
